import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';

interface Point {
  x: number;
  y: number;
}

/**
 * Directive that implements the "menu aim" / predictability-triangle pattern
 * on a top-level navigation item with a submenu. When the cursor leaves the
 * host element, instead of immediately hiding the submenu it checks whether
 * the cursor is traveling toward the open submenu panel. If so, it keeps the
 * submenu visible until the cursor either enters the submenu or moves outside
 * the aim corridor.
 *
 * The triangle is formed between the previous cursor position and the
 * submenu's top edge (top-left → top-right), since the submenu opens
 * downward from the horizontal topbar nav. The hold is deterministic: it is
 * taken only when the last step heads down toward that edge and lands inside
 * the triangle; a sideways exit toward a sibling item, or an exit with no
 * known direction, hides the submenu at once. Entering another aimed item
 * cancels any hold still in progress (ENG-1893).
 *
 * Mouse tracking switches to a document-level listener while aiming so the
 * cursor can be followed across the hover gap between the host <li> and the
 * submenu panel.
 *
 * Apply to `<li>` elements that have a `.submenu` child.
 *
 * Desktop-only (≥1280px); on smaller viewports the menu is click-toggled and
 * this directive is inert.
 */
@Directive({
  selector: '[menuAim]',
  standalone: false,
})
export class MenuAimDirective implements OnInit, OnDestroy {

  private static readonly DESKTOP_BREAKPOINT = 1280;
  private static readonly RECHECK_INTERVAL_MS = 75;
  private static readonly MAX_AIM_DURATION_MS = 400;
  private static readonly TOLERANCE_PX = 30;

  /**
   * The one host whose submenu is currently open or held. Entering another
   * host drops it at once, so a held panel can never overlap the sibling's
   * submenu that CSS :hover opens (ENG-1893).
   */
  private static activeHost: MenuAimDirective | null = null;

  private lastPositions: Point[] = [];
  private aimTimeout: ReturnType<typeof setTimeout> | null = null;
  private aimStart = 0;
  private unlisten: (() => void)[] = [];
  private unlistenDocMove: (() => void) | null = null;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      const host = this.el.nativeElement;

      this.unlisten.push(
        this.renderer.listen(host, 'mouseenter', (e: MouseEvent) => this.onMouseEnter(e)),
        this.renderer.listen(host, 'mouseleave', (e: MouseEvent) => this.onMouseLeave(e)),
        this.renderer.listen(host, 'mousemove', (e: MouseEvent) => this.trackMouse(e)),
      );
    });
  }

  ngOnDestroy(): void {
    this.deactivate();
    this.unlisten.forEach(fn => fn());
    this.unlisten = [];
  }

  private get isDesktop(): boolean {
    return window.innerWidth >= MenuAimDirective.DESKTOP_BREAKPOINT;
  }

  private trackMouse(e: MouseEvent): void {
    this.pushPosition(e.clientX, e.clientY);
  }

  private pushPosition(x: number, y: number): void {
    this.lastPositions.push({ x, y });
    if (this.lastPositions.length > 4) {
      this.lastPositions.shift();
    }
  }

  /** Document-level mousemove handler, active only while aiming. */
  private onDocumentMouseMove = (e: MouseEvent): void => {
    this.pushPosition(e.clientX, e.clientY);
  };

  private startDocumentTracking(): void {
    if (this.unlistenDocMove) return;
    this.zone.runOutsideAngular(() => {
      this.unlistenDocMove = this.renderer.listen('document', 'mousemove', this.onDocumentMouseMove);
    });
  }

  private stopDocumentTracking(): void {
    if (this.unlistenDocMove) {
      this.unlistenDocMove();
      this.unlistenDocMove = null;
    }
  }

  private onMouseEnter(e: MouseEvent): void {
    this.clearAimTimeout();
    this.stopDocumentTracking();

    // A sibling's held panel must go the moment this one is entered, otherwise
    // both are visible while CSS :hover opens this submenu.
    const previous = MenuAimDirective.activeHost;
    if (previous && previous !== this) {
      previous.deactivate();
    }
    MenuAimDirective.activeHost = this;

    // Seed the track with the entry point so a leave always has a direction to
    // compare against, even when no mousemove fires on a fast pass-through.
    this.lastPositions = [{ x: e.clientX, y: e.clientY }];

    if (this.isDesktop) {
      this.el.nativeElement.classList.add('menu-aim-active');
    }
  }

  private onMouseLeave(e: MouseEvent): void {
    if (!this.isDesktop) {
      this.deactivate();
      return;
    }

    const submenuEl = this.el.nativeElement.querySelector(':scope > .submenu') as HTMLElement | null;
    if (!submenuEl) {
      this.deactivate();
      return;
    }

    const cursor: Point = { x: e.clientX, y: e.clientY };
    // Record the exit point: the aim test reads direction from the last two
    // tracked points, so the leave itself must be one of them.
    this.pushPosition(cursor.x, cursor.y);
    const submenuRect = submenuEl.getBoundingClientRect();

    if (this.isCursorAimingAtSubmenu(cursor, submenuRect)) {
      this.aimStart = Date.now();
      this.startDocumentTracking();
      this.scheduleRecheck(submenuEl);
    } else {
      this.deactivate();
    }
  }

  private scheduleRecheck(submenuEl: HTMLElement): void {
    this.aimTimeout = setTimeout(() => {
      const elapsed = Date.now() - this.aimStart;
      if (elapsed > MenuAimDirective.MAX_AIM_DURATION_MS) {
        this.deactivate();
        return;
      }

      const cursor = this.lastPositions[this.lastPositions.length - 1];
      if (!cursor) {
        this.deactivate();
        return;
      }

      const submenuRect = submenuEl.getBoundingClientRect();

      // If cursor has entered the submenu rect, hand off to CSS :hover
      if (this.isPointInRect(cursor, submenuRect)) {
        this.stopDocumentTracking();
        this.clearAimTimeout();
        return;
      }

      if (this.isCursorAimingAtSubmenu(cursor, submenuRect)) {
        this.scheduleRecheck(submenuEl);
      } else {
        this.deactivate();
      }
    }, MenuAimDirective.RECHECK_INTERVAL_MS);
  }

  /**
   * "Aiming" means the cursor is heading down toward the open panel: its last
   * step must not move away from the panel's top edge, and the current point
   * must sit inside the corridor from the previous point to that edge.
   *
   * Without a known direction (fewer than two tracked points) the answer is
   * no: holding on a guess is what made the hide feel random (ENG-1893).
   * A sideways move along the nav bar toward a sibling item is not aiming
   * either, whatever the triangle says at that height.
   */
  private isCursorAimingAtSubmenu(cursor: Point, submenuRect: DOMRect): boolean {
    if (this.lastPositions.length < 2) return false;

    const prev = this.lastPositions[this.lastPositions.length - 2];
    if (cursor.y < prev.y) return false;

    const tolerance = MenuAimDirective.TOLERANCE_PX;

    // Triangle vertices: previous cursor position → submenu top-left → submenu top-right.
    // The submenu opens downward (position: absolute; top: 100%) so the near
    // edge is the top of the submenu panel. The tolerance widens the edge
    // sideways only; lifting it would put the edge above the exit point and
    // make the corridor unreachable from inside the nav item.
    const topLeft: Point = { x: submenuRect.left - tolerance, y: submenuRect.top };
    const topRight: Point = { x: submenuRect.right + tolerance, y: submenuRect.top };

    return this.pointInTriangle(cursor, prev, topLeft, topRight);
  }

  private pointInTriangle(pt: Point, v1: Point, v2: Point, v3: Point): boolean {
    const d1 = this.sign(pt, v1, v2);
    const d2 = this.sign(pt, v2, v3);
    const d3 = this.sign(pt, v3, v1);
    const hasNeg = (d1 < 0) || (d2 < 0) || (d3 < 0);
    const hasPos = (d1 > 0) || (d2 > 0) || (d3 > 0);
    return !(hasNeg && hasPos);
  }

  private sign(p1: Point, p2: Point, p3: Point): number {
    return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  }

  private isPointInRect(pt: Point, rect: DOMRect): boolean {
    return pt.x >= rect.left && pt.x <= rect.right &&
           pt.y >= rect.top && pt.y <= rect.bottom;
  }

  private deactivate(): void {
    this.clearAimTimeout();
    this.stopDocumentTracking();
    this.el.nativeElement.classList.remove('menu-aim-active');
    this.lastPositions = [];
    if (MenuAimDirective.activeHost === this) {
      MenuAimDirective.activeHost = null;
    }
  }

  private clearAimTimeout(): void {
    if (this.aimTimeout != null) {
      clearTimeout(this.aimTimeout);
      this.aimTimeout = null;
    }
  }
}
